import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

function buildPageItems(page: number, pageCount: number): (number | "ellipsis")[] {
  const items: (number | "ellipsis")[] = [];
  const siblings = 1;
  const first = 1;
  const last = pageCount;

  const start = Math.max(first, page - siblings);
  const end = Math.min(last, page + siblings);

  if (start > first) {
    items.push(first);
    if (start > first + 1) items.push("ellipsis");
  }
  for (let i = start; i <= end; i++) items.push(i);
  if (end < last) {
    if (end < last - 1) items.push("ellipsis");
    items.push(last);
  }
  return items;
}

export interface DataPaginationProps {
  /** 1-indexed current page */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  itemLabel?: string;
  /** "simple" = Previous/Next only, "numbered" = page-number links */
  mode?: "simple" | "numbered";
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

export function DataPagination({
  page,
  pageCount,
  onPageChange,
  totalItems,
  itemLabel = "items",
  mode = "simple",
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  className,
}: DataPaginationProps) {
  const canPrevious = page > 1;
  const canNext = page < pageCount;

  return (
    <div
      className={cn(
        "flex flex-col-reverse items-center justify-between gap-4 py-4 sm:flex-row",
        className
      )}
    >
      <div className="text-sm text-muted-foreground">
        Page {page} of {Math.max(pageCount, 1)}
        {typeof totalItems === "number" && (
          <> ({totalItems} total {itemLabel})</>
        )}
      </div>

      <div className="flex items-center gap-4">
        {pageSize !== undefined && onPageSizeChange && (
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-9 w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {mode === "simple" ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canPrevious}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        ) : (
          <>
            {/* Below sm, the numbered row doesn't fit — fall back to Previous/Next */}
            <div className="flex items-center gap-2 sm:hidden">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrevious}
                onClick={() => onPageChange(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext}
                onClick={() => onPageChange(page + 1)}
              >
                Next
              </Button>
            </div>
            <Pagination className="mx-0 hidden w-auto sm:flex">
              <PaginationContent>
                <PaginationItem>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canPrevious}
                    onClick={() => onPageChange(page - 1)}
                  >
                    Previous
                  </Button>
                </PaginationItem>
                {buildPageItems(page, Math.max(pageCount, 1)).map((item, index) =>
                  item === "ellipsis" ? (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationLink
                        isActive={item === page}
                        onClick={(event) => {
                          event.preventDefault();
                          onPageChange(item);
                        }}
                      >
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canNext}
                    onClick={() => onPageChange(page + 1)}
                  >
                    Next
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </>
        )}
      </div>
    </div>
  );
}
